// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { EditArticleMaterialsOperation } from '@admintool/operation/edit-article-materials-operation';
import { AdmintoolMaterialsService, EditMaterialsInfo } from '@admintool/services/admintool-materials.service';
import { PropertyValueVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { AddArticlePropertyOperation } from './add-article-property-operation';
import { RemoveArticlePropertyOperation } from './remove-article-property-operation';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ProjectModel } from '@project/model/project.model';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ConnectionUtils } from '@3d-scene/model3d/util/connection-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionCreator, ConnectionShapeType } from '@article/components/connections/connection-creation/connection-creator';
import { ConnectionConverter } from '@article/components/connections/connection-creation/connection-converter';
import { AdminService } from '../services/admin.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { AddConnectionPropertyOperation } from './add-connection-property-operation';
import { RemoveConnectionPropertyOperation } from './remove-connection-property-operation';
import { ChangeArticlePropertyOperation } from './change-article-property-operation';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ChangeConnectionCorridorOperation } from './change-connection-corridor-operation';
import { ProjectArticleManager } from '@project/components/article/manager/project-article.manager';
import { ChangeConnectionImageOperation } from './change-connection-image-operation';
import { ChangeConnectionNameOperation } from './change-connection-name-operation';
import { NumberUtils } from '@shared/util/number-utils';
import { ChangeConnectionOrderNumberOperation } from './change-connection-order-number-operation';
import { ChangeConnectionPropertyOperation } from './change-connection-property-operation';
import { ChangeConnectionShapeTypeOperation } from './change-connection-shape-type-operation';
import { ChangeConnectionTransparencyOperation } from './change-connection-transparency-operation';
import { ConnectionDefinition } from '@article/components/connections/connection-creation/connection-configuration';
import { ChangeConnectionTypeOperation } from './change-connection-type-operation';
import { ChangePropertyValuesOperation } from './change-property-values-operation';
import { ColorchangeConnectionOperation } from './colorchange-connection-operation';
import { CopyFromOperationState } from '../components/article/copy-properties-modal/copy-from-operation-state';
import { CopyArticlePropertiesFromOperation, CopyProperties } from './copy-article-properties-from-operation';
import { CopyConnectionItem } from '../components/article/copy-properties-modal/copy-connection-item';
import { ChangeConnectionSubTypeOperation } from './copy-connection-subtype-operation';
import { MoveConnectionOperation } from './move-connection-operation';
import { ChangeConnectionMultiplicityOperation } from './change-connection-multiplicity-operation';
import { AddConnectionOperation } from './add-connection-operation';
import { RemoveConnectionOperation } from './remove-connection-operation';
import { ArticleModelDataHolder } from '@3d-scene/model3d/cache/model-data';
import { MetaElementUsageVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ReplaceArticleModelOperation } from './replace-model-operation';
import { Mesh } from 'three';
import { TextureElementVO } from '@article/model/vo/texture-element.vo';
import { UndoTextureElementOperation } from './undo-texture-element-operation';
import { IOperation } from '@shared/undo/operation/i-operation';

@Injectable({providedIn: 'root'})
export class AdmintoolOperationFactory {

  constructor(
    private _projectModel: ProjectModel,
    private _selectionModel: SelectionModel,
    private _selectionModelService: SelectionModelService,
    private _sceneManager: SceneManager,
    private _articleContainerFactory: ArticleContainerFactory,
    private _connectionUtils: ConnectionUtils,
    private _componentUtils: ContainerService,
    private _connectionCreator: ConnectionCreator,
    private _connectionConverter: ConnectionConverter,
    private _adminService: AdminService,
    private _applicationRefreshService: ApplicationRefreshService,
    private _projectArticleManager: ProjectArticleManager,
    private _admintoolMaterialsService: AdmintoolMaterialsService,
  ) {}

  public createArticlePropertyOperation(property: PropertyVO, article: ArticleVO | undefined, propertyType: string, operationType: string): IOperation {
    if (!property || !article || !propertyType) {
      return null;
    }

    switch (operationType) {
      case UndoRedoUtils.ADD:
        return new AddArticlePropertyOperation(property, article, propertyType, this._connectionUtils, this._projectModel, this._adminService);
      case UndoRedoUtils.REMOVE:
        return new RemoveArticlePropertyOperation(property, article, propertyType, this._projectModel, this._connectionUtils, this._adminService);
      default:
        return null;
    }
  }

  public createConnectionPropertyOperation(
    property: PropertyVO | undefined,
    connectionName: string | undefined,
    article: ArticleVO | undefined,
    propertyType: string,
    operationType: string
  ): IOperation {
    if (!property || !connectionName || !article || !propertyType) {
      return null;
    }

    switch (operationType) {
      case UndoRedoUtils.ADD:
        return new AddConnectionPropertyOperation(
          property,
          connectionName,
          article,
          propertyType,
          this._projectModel,
          this._connectionUtils,
          this._adminService,
        );
      case UndoRedoUtils.REMOVE:
        return new RemoveConnectionPropertyOperation(
          property,
          connectionName,
          article,
          propertyType,
          this._projectModel,
          this._connectionUtils,
          this._adminService,
        );
      default:
        return null;
    }
  }

  public createArticlePropertyChangedOperation(
    oldProperty: PropertyVO,
    newProperty: PropertyVO,
    propertyType: string,
    originalPropertyType: string,
    article: ArticleVO
  ): ChangeArticlePropertyOperation {
    if (!oldProperty || !newProperty || !propertyType || !article) {
      return null;
    }

    return new ChangeArticlePropertyOperation(
      oldProperty,
      newProperty,
      article,
      propertyType,
      originalPropertyType,
      this._projectModel,
      this._connectionUtils,
      this._adminService,
    );
  }

    public createChangeConnectionCorridorOperation(
        connection: Connection,
        articleContainer: ArticleContainer,
        article: ArticleVO,
        oldCorridor: number,
        newCorridor: number,
        connections: Connection[]
    ) {
        if (!connection || !articleContainer || !article || !connections || oldCorridor === newCorridor) {
            return null;
        }
        return new ChangeConnectionCorridorOperation(
            connection,
            articleContainer,
            article,
            this._adminService,
            this._projectModel,
            this._selectionModel,
            this._selectionModelService,
            this._sceneManager,
            this._projectArticleManager,
            this._connectionCreator,
            this._connectionConverter,
            oldCorridor,
            newCorridor,
            this._applicationRefreshService,
            connections
        );
    }

  public createUndoTextureElementOperation(
    articleContainer: ArticleContainer,
    oldMeshes: Mesh[],
    newMeshes: Mesh[],
    oldTextureElements: TextureElementVO[],
    newTextureElements: TextureElementVO[],
    operationDescription: string
  ): UndoTextureElementOperation {
    if (!articleContainer || !oldMeshes || !newMeshes || !oldTextureElements || !newTextureElements) {
      return null;
    }

    return new UndoTextureElementOperation(
      this._projectModel,
      this._selectionModelService,
      this._sceneManager,
      this._applicationRefreshService,
      articleContainer,
      oldMeshes,
      newMeshes,
      oldTextureElements,
      newTextureElements,
      operationDescription);
  }

  public createChangeConnectionImageOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldImage: HTMLImageElement,
      newImage: HTMLImageElement,
      connections: Connection[]
  ) {
    if (!connection || !articleContainer || !article || !connections || !newImage) {
      return null;
    }

    return new ChangeConnectionImageOperation(
        connection,
        articleContainer,
        article,
        oldImage,
        newImage,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createChangeConnectionNameOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldConnectionName: string,
      newConnectionName,
      connections: Connection[]
  ) {
    if (!connection || !articleContainer || !article || !connections || !oldConnectionName || !newConnectionName) {
      return null;
    }

    return new ChangeConnectionNameOperation(
        connection,
        articleContainer,
        article,
        oldConnectionName,
        newConnectionName,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createChangeConnectionOrderNumberOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldOrderNumber: number,
      newOrderNumber: number,
      connections: Connection[]
  ) {
    if (
        !connection ||
        !articleContainer ||
        !article ||
        !connections ||
        NumberUtils.isNotNumber(oldOrderNumber) ||
        NumberUtils.isNotNumber(newOrderNumber)
    ) {
      return null;
    }

    return new ChangeConnectionOrderNumberOperation(
        connection,
        articleContainer,
        article,
        oldOrderNumber,
        newOrderNumber,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createConnectionPropertyChangedOperation(
      oldProperty: PropertyVO,
      newProperty: PropertyVO,
      propertyType: string,
      originalPropertyType: string,
      connectionName: string,
      article: ArticleVO
  ): ChangeConnectionPropertyOperation {
    if (!oldProperty || !newProperty || !propertyType || !connectionName || !article) {
      return null;
    }

    return new ChangeConnectionPropertyOperation(
        oldProperty,
        newProperty,
        connectionName,
        article,
        propertyType,
        originalPropertyType,
        this._projectModel,
        this._connectionUtils,
        this._adminService,
    );
  }

  public createChangeConnectionShapeTypeOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldShape: ConnectionShapeType,
      newShape: ConnectionShapeType,
      connections: Connection[]
  ) {
    if (!connection || !articleContainer || !article || !connections || !oldShape || !newShape) {
      return null;
    }

    return new ChangeConnectionShapeTypeOperation(
        connection,
        articleContainer,
        article,
        oldShape,
        newShape,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createChangeConnectionTransparencyOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldTransparency: number,
      newTransparency: number,
      connections: Connection[]
  ) {
    if (
        !connection ||
        !articleContainer ||
        !article ||
        !connections ||
        NumberUtils.isNotNumber(oldTransparency) ||
        NumberUtils.isNotNumber(newTransparency)
    ) {
      return null;
    }

    return new ChangeConnectionTransparencyOperation(
        connection,
        articleContainer,
        article,
        oldTransparency,
        newTransparency,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createChangeConnectionTypeOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldConnectionDefinition: ConnectionDefinition,
      newConnectionDefinition: ConnectionDefinition,
      connections: Connection[]
  ) {
    if (!connection || !articleContainer || !article || !connections || !oldConnectionDefinition || !newConnectionDefinition) {
      return null;
    }

    return new ChangeConnectionTypeOperation(
        connection,
        articleContainer,
        article,
        oldConnectionDefinition,
        newConnectionDefinition,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createChangePropertyValuesOperation(property: PropertyVO, propertyType: string, oldValues: PropertyValueVO[], newValues: PropertyValueVO[]) {
    if (!property || !propertyType) {
      return null;
    }

    return new ChangePropertyValuesOperation(
        property,
        propertyType,
        oldValues,
        newValues,
        this._projectModel,
        this._connectionUtils,
        this._adminService,
    );
  }

  public createColorchangeConnectionOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      connectionColor: string,
      connections: Connection[]
  ) {
    if (!connection || !articleContainer || !article || !connections || !connectionColor) {
      return null;
    }

    return new ColorchangeConnectionOperation(
        connection,
        articleContainer,
        article,
        connectionColor,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createCopyArticlePropertiesFromOperation(
      sourceArticleVO: ArticleVO,
      targetArticleContainer: ArticleContainer,
      targetConnectionList: CopyConnectionItem[],
      copyProperties: CopyProperties,
      oldState: CopyFromOperationState,
      newState: CopyFromOperationState
  ): CopyArticlePropertiesFromOperation {
    return new CopyArticlePropertiesFromOperation(
        sourceArticleVO.identity,
        targetArticleContainer,
        targetConnectionList,
        oldState,
        newState,
        copyProperties,
        this._projectModel,
        this._projectArticleManager,
        this._selectionModelService,
        this._sceneManager,
        this._articleContainerFactory,
        this._connectionUtils,
        this._componentUtils,
        this._connectionCreator,
        this._connectionConverter,
        this._adminService,
        this._applicationRefreshService
    );
  }

  public createChangeConnectionSubTypeOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldConnectionDefinition: ConnectionDefinition,
      newConnectionDefinition: ConnectionDefinition,
      connections: Connection[]
  ) {
    if (!connection || !articleContainer || !article || !connections || !oldConnectionDefinition || !newConnectionDefinition) {
      return null;
    }

    return new ChangeConnectionSubTypeOperation(
        connection,
        articleContainer,
        article,
        oldConnectionDefinition,
        newConnectionDefinition,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService,
        connections
    );
  }

  public createMoveConnectionOperation(
      articleContainer: ArticleContainer,
      connection: Connection,
      article: ArticleVO,
      oldConnectionPosition: any,
      newConnectionPosition: any,
      moveOperationType?: string
  ) {
    if (!articleContainer || !connection || !article || !oldConnectionPosition || !newConnectionPosition) {
      return null;
    }

    return new MoveConnectionOperation(
        articleContainer,
        connection,
        article,
        oldConnectionPosition,
        newConnectionPosition,
        moveOperationType,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        this._applicationRefreshService
    );
  }

  public createChangeConnectionMultiplicityOperation(
      connection: Connection,
      articleContainer: ArticleContainer,
      article: ArticleVO,
      oldMultiplicity: number,
      newMultiplicity: number,
      connections: Connection[]
  ) {
    if (
        !connection ||
        !articleContainer ||
        !article ||
        !connections ||
        NumberUtils.isNotNumber(oldMultiplicity) ||
        NumberUtils.isNotNumber(newMultiplicity)
    ) {
      return null;
    }

    return new ChangeConnectionMultiplicityOperation(
        connection,
        articleContainer,
        article,
        this._adminService,
        this._projectModel,
        this._selectionModel,
        this._selectionModelService,
        this._sceneManager,
        this._projectArticleManager,
        this._connectionCreator,
        this._connectionConverter,
        oldMultiplicity,
        newMultiplicity,
        this._applicationRefreshService,
        connections
    );
  }

  public createConnectionRemoveOperation(connection: Connection, articleContainer: ArticleContainer, article: ArticleVO): RemoveConnectionOperation {
    if (!connection || !articleContainer || !article) {
      return null;
    }
    return new RemoveConnectionOperation(
      this._adminService,
      this._projectModel,
      this._selectionModel,
      this._selectionModelService,
      this._sceneManager,
      this._projectArticleManager,
      this._connectionCreator,
      this._connectionConverter,
      connection,
      articleContainer,
      articleContainer.article,
      this._applicationRefreshService
    );
  }

  public createConnectionAddOperation(connection: Connection, articleContainer: ArticleContainer, article: ArticleVO): AddConnectionOperation {
    if (!connection || !articleContainer || !article) {
      return null;
    }
    return new AddConnectionOperation(
      this._adminService,
      this._projectModel,
      this._selectionModel,
      this._selectionModelService,
      this._sceneManager,
      this._projectArticleManager,
      this._connectionCreator,
      this._connectionConverter,
      connection,
      articleContainer,
      article,
      this._applicationRefreshService
    );
  }

  public createReplaceArticleModelOperation(
      articleContainer: ArticleContainer,
      oldModelDataHolder: ArticleModelDataHolder,
      newModelDataHolder: ArticleModelDataHolder,
      removedMetaElements: MetaElementVO[],
      removedMetaElementUsages: MetaElementUsageVO[],
      removedTextureElements: TextureElementVO[],
      keepConnections: boolean
  ): ReplaceArticleModelOperation {
    if (!articleContainer || !oldModelDataHolder || !newModelDataHolder || !removedMetaElements || !removedMetaElementUsages || !removedTextureElements) {
      return null;
    }
    return new ReplaceArticleModelOperation(
        this._projectModel,
        this._selectionModelService,
        this._sceneManager,
        this._articleContainerFactory,
        this._componentUtils,
        articleContainer,
        oldModelDataHolder,
        newModelDataHolder,
        removedMetaElements,
        removedMetaElementUsages,
        removedTextureElements,
        keepConnections,
        this._applicationRefreshService
    );
  }

  public createEditArticleMaterialsOperation(
    oldMaterialsInfo: EditMaterialsInfo[],
    newMaterialsInfo: EditMaterialsInfo[]
  ): EditArticleMaterialsOperation {
    return new EditArticleMaterialsOperation(this._admintoolMaterialsService, oldMaterialsInfo, newMaterialsInfo)
  }
}
